'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import StackTrace from 'stacktrace-js';

/**
 * Hook to capture global JavaScript errors and unhandled promise rejections
 * Uses stacktrace-js to decode minified stack traces using source maps
 * Add this to your root layout or AppWrapper
 */
export function useErrorReporting() {
  const $ = _c(10);
  const pathname = usePathname();
  const sessionStartTime = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const navigationHistory = useRef(t0);
  const lastUserAction = useRef(null);
  const pageLoadTime = useRef(null);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (sessionStartTime.current === null) {
        sessionStartTime.current = Date.now();
      }
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== pathname) {
    t3 = () => {
      const now = Date.now();
      navigationHistory.current = [...navigationHistory.current.slice(-9), {
        path: pathname,
        timestamp: now
      }];
      pageLoadTime.current = now;
      const reloadKey = "server_action_reload_" + pathname;
      if (sessionStorage.getItem(reloadKey)) {
        sessionStorage.removeItem(reloadKey);
      }
    };
    t4 = [pathname];
    $[3] = pathname;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const trackAction = type => event => {
        const target = event.target;
        const tagName = target?.tagName?.toLowerCase() || "unknown";
        const className = target?.className?.toString?.()?.slice(0, 50) || "";
        const id = target?.id || "";
        const text = target?.innerText?.slice(0, 30) || "";
        lastUserAction.current = {
          type,
          element: `${tagName}${id ? "#" + id : ""}${className ? "." + className.split(" ")[0] : ""}`,
          text,
          timestamp: Date.now()
        };
      };
      document.addEventListener("click", trackAction("click"), {
        capture: true,
        passive: true
      });
      document.addEventListener("submit", trackAction("submit"), {
        capture: true,
        passive: true
      });
      document.addEventListener("keydown", trackAction("keydown"), {
        capture: true,
        passive: true
      });
      return () => {
        document.removeEventListener("click", trackAction("click"), {
          capture: true
        });
        document.removeEventListener("submit", trackAction("submit"), {
          capture: true
        });
        document.removeEventListener("keydown", trackAction("keydown"), {
          capture: true
        });
      };
    };
    t6 = [];
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const collectDiagnostics = () => {
        const ua = navigator.userAgent;
        const diagnostics = {
          safariVersion: /Version\/(\d+\.\d+(\.\d+)?)/.exec(ua)?.[1] || null,
          webkitVersion: /AppleWebKit\/(\d+\.\d+)/.exec(ua)?.[1] || null,
          chromeVersion: /Chrome\/(\d+\.\d+)/.exec(ua)?.[1] || null,
          firefoxVersion: /Firefox\/(\d+\.\d+)/.exec(ua)?.[1] || null,
          macOSVersion: /Mac OS X (\d+[._]\d+([._]\d+)?)/.exec(ua)?.[1]?.replace(/_/g, ".") || null,
          iOSVersion: /OS (\d+_\d+(_\d+)?)/.exec(ua)?.[1]?.replace(/_/g, ".") || null,
          deviceMemory: navigator.deviceMemory || "unknown",
          hardwareConcurrency: navigator.hardwareConcurrency || "unknown",
          maxTouchPoints: navigator.maxTouchPoints || 0,
          connectionType: navigator.connection?.effectiveType || "unknown",
          connectionDownlink: navigator.connection?.downlink || "unknown",
          onLine: navigator.onLine,
          documentVisibilityState: document.visibilityState,
          documentReadyState: document.readyState,
          wasPageRestored: performance.getEntriesByType?.("navigation")?.[0]?.type === "back_forward",
          sessionDurationMs: sessionStartTime.current ? Date.now() - sessionStartTime.current : null,
          timeOnPageMs: pageLoadTime.current ? Date.now() - pageLoadTime.current : null,
          pageLoadTimestamp: pageLoadTime.current ? new Date(pageLoadTime.current).toISOString() : null,
          navigationHistoryCount: navigationHistory.current.length,
          navigationHistory: navigationHistory.current.map(_temp),
          lastUserAction: lastUserAction.current ? {
            ...lastUserAction.current,
            secondsAgo: Math.round((Date.now() - lastUserAction.current.timestamp) / 1000)
          } : null,
          jsHeapSizeLimit: performance.memory?.jsHeapSizeLimit || null,
          usedJSHeapSize: performance.memory?.usedJSHeapSize || null,
          totalJSHeapSize: performance.memory?.totalJSHeapSize || null,
          activeElementTag: document.activeElement?.tagName?.toLowerCase() || null,
          documentHasFocus: document.hasFocus(),
          windowInnerWidth: window.innerWidth,
          windowInnerHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
          performanceNow: Math.round(performance.now()),
          potentialIssues: []
        };
        if (diagnostics.wasPageRestored) {
          diagnostics.potentialIssues.push("Page was restored from back/forward cache (bfcache)");
        }
        if (diagnostics.documentVisibilityState === "hidden") {
          diagnostics.potentialIssues.push("Page was hidden when error occurred");
        }
        if (diagnostics.sessionDurationMs > 3600000) {
          diagnostics.potentialIssues.push("Long session (>1 hour)");
        }
        if (diagnostics.navigationHistoryCount > 5) {
          diagnostics.potentialIssues.push("Heavy navigation (5+ pages this session)");
        }
        if (diagnostics.usedJSHeapSize && diagnostics.jsHeapSizeLimit) {
          const heapUsage = diagnostics.usedJSHeapSize / diagnostics.jsHeapSizeLimit;
          if (heapUsage > 0.8) {
            diagnostics.potentialIssues.push(`High memory usage (${Math.round(heapUsage * 100)}%)`);
          }
        }
        return diagnostics;
      };
      const logError = _temp2;
      const formatStackFrames = _temp4;
      const getDecodedStack = async error => {
        if (!error) {
          return "No error object available";
        }
        ;
        try {
          const frames_0 = await StackTrace.fromError(error);
          return formatStackFrames(frames_0);
        } catch (t9) {
          const e_0 = t9;
          console.warn("Failed to decode stack trace:", e_0);
          return error.stack || "Stack trace unavailable";
        }
      };
      const handleError = async event_0 => {
        const decodedStack = await getDecodedStack(event_0.error);
        const diagnostics_0 = collectDiagnostics();
        logError({
          message: event_0.message || "Unknown error",
          stack: decodedStack,
          rawStack: event_0.error?.stack || `${event_0.filename}:${event_0.lineno}:${event_0.colno}`,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          type: "window",
          diagnostics: diagnostics_0
        });
      };
      const handleRejection = async event_1 => {
        const error_0 = event_1.reason;
        const errorMessage = error_0?.message || String(error_0) || "";
        if (errorMessage.includes("was not found on the server") || errorMessage.includes("failed-to-find-server-action")) {
          const reloadKey_0 = "server_action_reload_" + window.location.pathname;
          if (sessionStorage.getItem(reloadKey_0)) {
            console.error("Server Action mismatch persists after reload - not retrying");
            sessionStorage.removeItem(reloadKey_0);
          } else {
            console.warn("Server Action mismatch detected - reloading page to get fresh code");
            sessionStorage.setItem(reloadKey_0, Date.now().toString());
            event_1.preventDefault();
            window.location.reload();
            return;
          }
        }
        const decodedStack_0 = error_0 instanceof Error ? await getDecodedStack(error_0) : "";
        const diagnostics_1 = collectDiagnostics();
        logError({
          message: errorMessage || "Unhandled promise rejection",
          stack: decodedStack_0,
          rawStack: error_0?.stack || "",
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          type: "unhandledrejection",
          diagnostics: diagnostics_1
        });
      };
      window.addEventListener("error", handleError);
      window.addEventListener("unhandledrejection", handleRejection);
      return () => {
        window.removeEventListener("error", handleError);
        window.removeEventListener("unhandledrejection", handleRejection);
      };
    };
    t8 = [];
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
}
function _temp4(frames) {
  return frames.map(_temp3).join("\n");
}
function _temp3(frame) {
  const fileName = frame.fileName || "unknown";
  const lineNumber = frame.lineNumber || "?";
  const columnNumber = frame.columnNumber || "?";
  const functionName = frame.functionName || "(anonymous)";
  return `    at ${functionName} (${fileName}:${lineNumber}:${columnNumber})`;
}
async function _temp2(errorData) {
  ;
  try {
    await fetch("/api/log-client-error", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(errorData)
    });
  } catch (t0) {
    const e = t0;
    console.error("Failed to report error:", e);
  }
}
function _temp(n) {
  return {
    path: n.path,
    secondsAgo: Math.round((Date.now() - n.timestamp) / 1000)
  };
}